import { FormEventHandler, ReactNode, UIEvent, useRef } from 'react';
import { Card } from './ui';
import { cn } from '../lib/utils';

type PageShellProps = {
  children: ReactNode;
  className?: string;
};

/** Full-height page column so tables can fill remaining viewport. */
export function PageShell({ children, className }: PageShellProps) {
  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-4', className)}>
      {children}
    </div>
  );
}

type FormPageShellProps = {
  children: ReactNode;
  /** Sticky action row (Cancel / Save). */
  footer?: ReactNode;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  className?: string;
  contentClassName?: string;
  /** Drop max-width so the page uses the full main column. */
  wide?: boolean;
};

/**
 * Full-height scrollable form/detail layout.
 * Body scrolls inside the main area; footer stays pinned.
 */
export function FormPageShell({
  children,
  footer,
  onSubmit,
  className,
  contentClassName,
  wide = false,
}: FormPageShellProps) {
  const body = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-thin">
        <div
          className={cn(
            'mx-auto w-full space-y-6 px-0.5 pb-6',
            wide ? 'max-w-none' : 'max-w-4xl',
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>
      {footer ? (
        <div className="shrink-0 border-t border-line bg-surface-card/95 py-3 backdrop-blur-sm">
          <div
            className={cn(
              'mx-auto flex justify-end gap-2 px-0.5',
              wide ? 'max-w-none' : 'max-w-4xl',
            )}
          >
            {footer}
          </div>
        </div>
      ) : null}
    </>
  );

  if (onSubmit) {
    return (
      <form
        onSubmit={onSubmit}
        className={cn('flex h-full min-h-0 flex-col', className)}
      >
        {body}
      </form>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>{body}</div>
  );
}

type DataTablePanelProps = {
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Extra classes on the scrollable body. */
  bodyClassName?: string;
};

/**
 * Table card that grows to fill remaining page height.
 * Body scrolls; footer (pagination) stays pinned.
 */
export function DataTablePanel({
  children,
  footer,
  className,
  bodyClassName,
}: DataTablePanelProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  function syncHorizontalScroll(source: HTMLDivElement, target: HTMLDivElement) {
    if (target.scrollLeft !== source.scrollLeft) {
      target.scrollLeft = source.scrollLeft;
    }
  }

  function onBodyScroll(event: UIEvent<HTMLDivElement>) {
    const footerEl = footerRef.current;
    if (footerEl) syncHorizontalScroll(event.currentTarget, footerEl);
  }

  function onFooterScroll(event: UIEvent<HTMLDivElement>) {
    const bodyEl = bodyRef.current;
    if (bodyEl) syncHorizontalScroll(event.currentTarget, bodyEl);
  }

  return (
    <Card
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden',
        className,
      )}
    >
      <div
        ref={bodyRef}
        className={cn(
          'min-h-0 flex-1 overflow-auto overscroll-contain scrollbar-thin',
          bodyClassName,
        )}
        onScroll={onBodyScroll}
      >
        {children}
      </div>
      {footer ? (
        <div
          ref={footerRef}
          className="shrink-0 overflow-x-auto overflow-y-hidden overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={onFooterScroll}
        >
          {footer}
        </div>
      ) : null}
    </Card>
  );
}

/** Sticky table header row classes for scrollable panels. */
export const dataTableHeadClass =
  'sticky top-0 z-10 bg-surface-muted/95 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted backdrop-blur-sm shadow-[inset_0_-1px_0_0_rgb(var(--bf-line))]';

/** Sticky table footer row classes so column sums stay visible. */
export const dataTableFootClass =
  'sticky bottom-0 z-10 bg-surface-muted/95 font-medium backdrop-blur-sm shadow-[inset_0_1px_0_0_rgb(var(--bf-line))]';
